import { useEffect, useState } from "react";
import { api } from "../api/client";
import { PeriodPicker } from "../components/PeriodPicker";
import {
  FlagDetailsResponse,
  FlagsResponse,
  JournalResponse,
  MeResponse,
  OperatorStatsResponse,
} from "../types";

interface Props {
  me: MeResponse;
}

type Section = "flags" | "journal" | "operators";

const SEVERITY_BG: Record<string, string> = {
  high: "bg-red-100 text-red-800 border-red-300",
  medium: "bg-amber-100 text-amber-800 border-amber-300",
  low: "bg-slate-100 text-slate-700 border-slate-300",
};

export function AuditPage({ me: _me }: Props) {
  const [section, setSection] = useState<Section>("flags");
  const [period, setPeriod] = useState("this_week");
  const [customStart, setCustomStart] = useState<string | null>(null);
  const [customEnd, setCustomEnd] = useState<string | null>(null);

  // Сериализуем кастомный диапазон в query-параметры для backend.
  const periodQS = (() => {
    const qs = new URLSearchParams({ periodKey: period });
    if (period === "custom" && customStart && customEnd) {
      qs.set("customStart", customStart);
      qs.set("customEnd", customEnd);
    }
    return qs.toString();
  })();

  return (
    <div>
      <h1 className="text-xl font-semibold mb-3">🚨 Аудит</h1>

      <div className="mb-3">
        <label className="text-xs text-tg-hint">Период</label>
        <PeriodPicker
          value={period}
          onChange={setPeriod}
          customStart={customStart}
          customEnd={customEnd}
          onCustomChange={(s, e) => {
            setCustomStart(s);
            setCustomEnd(e);
          }}
        />
      </div>

      <div className="flex gap-2 mb-4 overflow-x-auto -mx-4 px-4">
        {(["flags", "journal", "operators"] as Section[]).map((s) => (
          <button
            key={s}
            onClick={() => setSection(s)}
            className={
              "px-3 py-1.5 rounded-full text-sm whitespace-nowrap " +
              (section === s
                ? "bg-tg-button text-tg-buttonText"
                : "bg-tg-secondaryBg text-tg-text")
            }
          >
            {s === "flags" ? "Флаги" : s === "journal" ? "Журнал" : "Операторы"}
          </button>
        ))}
      </div>

      {section === "flags" && <FlagsView periodQS={periodQS} />}
      {section === "journal" && (
        <JournalView
          period={period}
          customStart={customStart}
          customEnd={customEnd}
          periodQS={periodQS}
        />
      )}
      {section === "operators" && (
        <OperatorsView period={period} customStart={customStart} customEnd={customEnd} periodQS={periodQS} />
      )}
    </div>
  );
}

function FlagsView({ periodQS }: { periodQS: string }) {
  const [data, setData] = useState<FlagsResponse | null>(null);
  const [details, setDetails] = useState<FlagDetailsResponse | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);

  useEffect(() => {
    setData(null);
    setDetails(null);
    api.get<FlagsResponse>(`/api/audit/flags?${periodQS}`).then(setData);
  }, [periodQS]);

  const openFlag = async (id: string) => {
    setLoadingId(id);
    try {
      const r = await api.get<FlagDetailsResponse>(
        `/api/audit/flags/${id}?${periodQS}`,
      );
      setDetails(r);
    } finally {
      setLoadingId(null);
    }
  };

  if (!data) return <div className="text-tg-hint">Загрузка…</div>;

  if (details) {
    return (
      <div>
        <button
          onClick={() => setDetails(null)}
          className="text-tg-accentText text-sm mb-3"
        >
          ← Назад
        </button>
        <div
          className={
            "rounded-lg px-3 py-1.5 inline-block text-xs border mb-3 " +
            SEVERITY_BG[details.severity]
          }
        >
          {details.label} · {details.items.length}
        </div>
        <div className="space-y-2">
          {details.items.map((it, i) => (
            <div key={`${it.deal_id}-${i}`} className="bg-tg-sectionBg rounded-lg p-3">
              <div className="text-sm font-medium">
                {it.deal_id ? `№${it.deal_id} · ` : ""}
                {it.title}
              </div>
              <div className="text-xs text-tg-hint mt-1">{it.detail}</div>
            </div>
          ))}
          {details.items.length === 0 && (
            <div className="py-6 text-center text-tg-hint text-sm">
              Нет срабатываний за период.
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="text-xs text-tg-hint mb-2">{data.period_label}</div>
      <div className="bg-tg-sectionBg rounded-xl divide-y divide-tg-secondaryBg overflow-hidden">
        {data.flags.map((f) => (
          <button
            key={f.id}
            onClick={() => openFlag(f.id)}
            disabled={loadingId === f.id || f.count === 0}
            className="w-full px-4 py-3 text-left flex items-center justify-between gap-3 disabled:opacity-50"
          >
            <div className="min-w-0">
              <div className="text-sm font-medium">{f.label}</div>
              <div className="text-[11px] text-tg-hint truncate">
                {f.description}
              </div>
            </div>
            <span
              className={
                "shrink-0 rounded-full px-2 py-0.5 text-xs border " +
                SEVERITY_BG[f.severity]
              }
            >
              {f.count}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function JournalView({
  period: _period,
  customStart: _customStart,
  customEnd: _customEnd,
  periodQS,
}: {
  period: string;
  customStart: string | null;
  customEnd: string | null;
  periodQS: string;
}) {
  const [data, setData] = useState<JournalResponse | null>(null);
  const [action, setAction] = useState<string>("");

  useEffect(() => {
    setData(null);
    const qs = new URLSearchParams(periodQS);
    qs.set("limit", "200");
    if (action) qs.set("action", action);
    api.get<JournalResponse>(`/api/audit/journal?${qs}`).then(setData);
  }, [periodQS, action]);

  if (!data) return <div className="text-tg-hint">Загрузка…</div>;

  return (
    <div>
      <div className="mb-3">
        <select
          value={action}
          onChange={(e) => setAction(e.target.value)}
          className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-sm"
        >
          <option value="">Все действия</option>
          {data.actions.map((a) => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>
      </div>

      {data.patterns.length > 0 && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-3">
          <div className="text-xs font-medium text-amber-800 mb-1">⚠️ Паттерны</div>
          <ul className="text-xs space-y-1 text-amber-900">
            {data.patterns.map((p, i) => <li key={i}>{p}</li>)}
          </ul>
        </div>
      )}

      <div className="text-xs text-tg-hint mb-2">
        {data.period_label} · {data.entries.length} записей
      </div>

      <div className="space-y-2">
        {data.entries.map((e) => (
          <div key={e.id} className="bg-tg-sectionBg rounded-lg p-3">
            <div className="flex justify-between text-xs text-tg-hint">
              <span>{new Date(e.created_at).toLocaleString("ru-RU")}</span>
              <span>{e.actor_role || "—"}</span>
            </div>
            <div className="text-sm font-medium mt-0.5">{e.action}</div>
            <div className="text-xs text-tg-hint mt-0.5">
              {e.actor_name || `tg #${e.actor_telegram_id ?? "?"}`}
              {e.target_type && ` · ${e.target_type} #${e.target_id}`}
            </div>
            {e.payload && (
              <div className="text-[11px] text-tg-hint mt-1 font-mono">
                {e.payload.slice(0, 200)}
              </div>
            )}
          </div>
        ))}
      </div>

      <button
        onClick={() => {
          const params: Record<string, unknown> = { period_key: _period };
          if (_period === "custom" && _customStart && _customEnd) {
            params.custom = [_customStart, _customEnd];
          }
          if (action) params.action = action;
          api.download("audit_journal_xlsx", params, "journal.xlsx");
        }}
        className="w-full mt-4 px-3 py-2 rounded-lg bg-tg-secondaryBg text-sm"
      >
        📥 Скачать xlsx
      </button>
    </div>
  );
}

function OperatorsView({
  period, customStart, customEnd, periodQS,
}: {
  period: string;
  customStart: string | null;
  customEnd: string | null;
  periodQS: string;
}) {
  const [data, setData] = useState<OperatorStatsResponse | null>(null);

  useEffect(() => {
    setData(null);
    api.get<OperatorStatsResponse>(
      `/api/audit/operator_stats?${periodQS}`,
    ).then(setData);
  }, [periodQS]);

  if (!data) return <div className="text-tg-hint">Загрузка…</div>;

  return (
    <div>
      <div className="text-xs text-tg-hint mb-2">{data.period_label}</div>
      <div className="space-y-3">
        {data.stats.map((s) => (
          <div key={s.operator_id} className="bg-tg-sectionBg rounded-xl p-4">
            <div className="font-medium">{s.operator_name}</div>
            <div className="text-xs text-tg-hint">tg #{s.operator_tg ?? "?"}</div>
            <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
              <Tile label="Всего" value={s.deals_total} />
              <Tile label="Импорт" value={s.deals_import} />
              <Tile label="Экспорт" value={s.deals_export} />
              <Tile label="Зафикс." value={s.fixed} />
              <Tile label="Перенос" value={s.postponed} />
              <Tile label="Подтв." value={s.confirmed} />
            </div>
            <div className="mt-2 text-xs text-tg-hint space-y-0.5">
              <div>% переносов: {s.pct_postpone.toFixed(4)}</div>
              <div>% правок после фикса: {s.pct_edit_after_fix.toFixed(4)}</div>
              {s.avg_create_to_fix_min != null && (
                <div>Среднее от создания до фикса: {s.avg_create_to_fix_min.toFixed(0)} мин</div>
              )}
              {s.avg_rate_dev_pct != null && (
                <div>Откл. от ЦБ: {s.avg_rate_dev_pct.toFixed(4)} %</div>
              )}
            </div>
          </div>
        ))}
      </div>
      <button
        onClick={() => {
          const params: Record<string, unknown> = { period_key: period };
          if (period === "custom" && customStart && customEnd) {
            params.custom = [customStart, customEnd];
          }
          api.download("operator_stats_xlsx", params, "operator_stats.xlsx");
        }}
        className="w-full mt-4 px-3 py-2 rounded-lg bg-tg-secondaryBg text-sm"
      >
        📥 Скачать xlsx
      </button>
    </div>
  );
}

function Tile({ label, value }: { label: string; value: number }) {
  return (
    <div className="bg-tg-secondaryBg rounded p-2">
      <div className="text-tg-hint">{label}</div>
      <div className="font-semibold text-sm">{value}</div>
    </div>
  );
}
